import React, { useMemo, useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { CssBaseline, ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material';
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './hooks/useAuth';
import { ThemeProvider } from './context/ThemeContext';
import { useAppTheme } from './hooks/useAppTheme';
import { ResponsiveBackground } from './components/common/ResponsiveBackground';
import { LoadingOverlay } from './components/common';
import { TokenRefreshWrapper } from './components/common/TokenRefreshWrapper';
import { AUTH_LOGIN_REDIRECT_EVENT } from './services/api';
import './App.css';
import { registerSW } from 'virtual:pwa-register'; // Import for PWA updates

const LoginPage = lazy(() => import('./pages/LoginPage').then((module) => ({ default: module.LoginPage })));
const RegisterPage = lazy(() => import('./pages/RegisterPage').then((module) => ({ default: module.RegisterPage })));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage').then((module) => ({ default: module.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage').then((module) => ({ default: module.ResetPasswordPage })));
const DashboardPage = lazy(() => import('./pages/DashboardPage').then((module) => ({ default: module.DashboardPage })));
const FlightsPage = lazy(() => import('./pages/FlightsPage').then((module) => ({ default: module.FlightsPage })));
const FlightDetailPage = lazy(() => import('./pages/FlightDetailPage').then((module) => ({ default: module.FlightDetailPage })));
const FlightEditPage = lazy(() => import('./pages/FlightEditPage').then((module) => ({ default: module.FlightEditPage })));
const NewFlightPage = lazy(() => import('./pages/NewFlightPage').then((module) => ({ default: module.NewFlightPage })));
const GlidersPage = lazy(() => import('./pages/GlidersPage').then((module) => ({ default: module.GlidersPage })));
const GoogleAuthCallback = lazy(() => import('./pages/GoogleAuthCallback').then((module) => ({ default: module.GoogleAuthCallback })));
const PublicFlightDetailPage = lazy(() => import('./pages/PublicFlightDetailPage').then((module) => ({ default: module.PublicFlightDetailPage })));
const SitesPage = lazy(() => import('./pages/SitesPage').then((module) => ({ default: module.SitesPage })));
const BaliseManagerPage = lazy(() => import('./pages/BaliseManagerPage').then((module) => ({ default: module.BaliseManagerPage })));
const WeatherStationsPage = lazy(() => import('./pages/WeatherStationsPage').then((module) => ({ default: module.WeatherStationsPage })));
const WeatherForecastPage = lazy(() => import('./pages/WeatherForecastPage').then((module) => ({ default: module.WeatherForecastPage })));
const MeteoSpotManagerPage = lazy(() => import('./pages/MeteoSpotManagerPage').then((module) => ({ default: module.MeteoSpotManagerPage })));
const ThermalHotspotsMapPage = lazy(() => import('./pages/ThermalHotspotsMapPage').then((module) => ({ default: module.ThermalHotspotsMapPage })));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage').then((module) => ({ default: module.PrivacyPolicyPage })));
const TermsOfUsePage = lazy(() => import('./pages/TermsOfUsePage').then((module) => ({ default: module.TermsOfUsePage })));

// Create the app with theme
const AppContent: React.FC = () => {
  const { theme } = useAppTheme();
  const [showReload, setShowReload] = useState(false);
  const [updateSW, setUpdateSW] = useState<((reloadPage?: boolean) => Promise<void>) | null>(null);

  useEffect(() => {
    const updateServiceWorker = registerSW({
      onNeedRefresh() {
        setShowReload(true);
        setUpdateSW(() => updateServiceWorker);
      },
      onOfflineReady() {
        console.log('PWA offline ready');
      },
    });
  }, []);

  const handleReload = () => {
    if (updateSW) {
      updateSW(true);
    }
  };
  
  // Create a theme
  const muiTheme = useMemo(() => createTheme({
    palette: {
      mode: theme === 'dark' ? 'dark' : 'light',
      primary: {
        main: '#64B5F6', // Bleu ciel, couleur du ciel en parapente
        dark: '#1976D2',
        light: '#90CAF9',
        contrastText: '#fff',
      },
      secondary: {
        main: '#FF9800', // Orange, couleur de nombreuses voiles de parapente
        dark: '#F57C00',
        light: '#FFB74D',
        contrastText: '#fff',
      },
      error: {
        main: '#F44336',
      },
      warning: {
        main: '#FFC107',
      },
      info: {
        main: '#29B6F6',
      },
      success: {
        main: '#66BB6A',
      },
      background: {
        default: 'transparent',
        paper: theme === 'dark' 
          ? 'rgba(42, 45, 66, 0.85)' // Bleu nuit semi-transparent pour thème sombre
          : 'rgba(255, 255, 255, 0.85)', // Blanc semi-transparent pour thème clair
      },
      text: {
        primary: theme === 'dark' ? '#ffffff' : '#333333',
        secondary: theme === 'dark' ? '#E0E0E0' : '#757575',
      },
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: theme === 'dark' 
              ? 'rgba(42, 45, 66, 0.85)' // Bleu nuit semi-transparent
              : 'rgba(255, 255, 255, 0.85)', // Blanc semi-transparent
            backdropFilter: 'blur(8px)', // Effet de flou amélioré
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            borderRadius: '12px',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: theme === 'dark' 
              ? 'rgba(42, 45, 66, 0.85)' // Bleu nuit semi-transparent
              : 'rgba(255, 255, 255, 0.85)', // Blanc semi-transparent
            backdropFilter: 'blur(8px)', // Effet de flou amélioré
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            borderRadius: '8px',
            color: theme === 'dark' ? '#ffffff' : '#333333', // Couleur du texte selon le thème
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '6px',
            textTransform: 'none',
            fontWeight: 500,
          },
          contained: {
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: theme === 'dark' ? '#90CAF9' : '#1976D2', // Couleur de lien selon le thème
            textDecoration: 'none',
            '&:hover': {
              color: theme === 'dark' ? '#64B5F6' : '#1565C0',
              textDecoration: 'underline',
            },
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            color: theme === 'dark' ? '#ffffff' : '#333333',
            '& a': {
              color: theme === 'dark' ? '#90CAF9' : '#1976D2',
              textDecoration: 'none',
              '&:hover': {
                color: theme === 'dark' ? '#64B5F6' : '#1565C0',
                textDecoration: 'underline',
              },
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#64B5F6', // Bleu ciel pour la bordure en focus
              borderWidth: 2,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(100, 181, 246, 0.7)', // Version plus claire au survol
            },
            '&.Mui-focused': {
              color: theme === 'dark' ? '#ffffff' : '#333333',
            },
            '& .MuiOutlinedInput-input': {
              color: theme === 'dark' ? '#ffffff' : '#333333',
            },
            '& .MuiInputAdornment-root .MuiSvgIcon-root': {
              color: theme === 'dark' ? '#90CAF9' : '#1976D2',
            },
          },
        },
      },
      // ...rest of the components styling
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontWeight: 700,
        fontSize: '2.5rem',
      },
      // ...rest of typography styling
    },
  }), [theme]);

  const routeFallback = <LoadingOverlay open={true} global />;

  return (
    <MuiThemeProvider theme={muiTheme}>
      <CssBaseline />
      <ResponsiveBackground overlay>
        {showReload && (
          <div style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '10px 20px',
            background: theme === 'dark' ? '#333' : '#fff',
            color: theme === 'dark' ? '#fff' : '#333',
            border: `1px solid ${theme === 'dark' ? '#555' : '#ccc'}`,
            borderRadius: '8px',
            boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
            zIndex: 10000, // Ensure it's on top
            display: 'flex',
            alignItems: 'center',
            gap: '15px'
          }}>
            <span>Une nouvelle version est disponible !</span>
            <button 
              onClick={handleReload}
              style={{
                padding: '8px 15px',
                border: 'none',
                borderRadius: '5px',
                backgroundColor: '#64B5F6', // Primary color from your theme
                color: '#fff',
                cursor: 'pointer',
                fontWeight: 500
              }}
            >
              Rafraîchir
            </button>
          </div>
        )}
        <div className="content-container">
          <BrowserRouter>
            <AuthNavigationListener />
            <AuthProvider>
              <TokenRefreshWrapper>
                <Suspense fallback={routeFallback}>
                <Routes>
                {/* Auth Routes */}
                <Route path="/login" element={
                  <AuthRoute>
                    <LoginPage />
                  </AuthRoute>
                } />
                <Route path="/register" element={
                  <AuthRoute>
                    <RegisterPage />
                  </AuthRoute>
                } />
                
                <Route path="/forgot-password" element={
                  <AuthRoute>
                    <ForgotPasswordPage />
                  </AuthRoute>
                } />
                
                <Route path="/reset-password" element={
                  <AuthRoute>
                    <ResetPasswordPage />
                  </AuthRoute>
                } />
                
                {/* Google Auth Callback Route */}
                <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />

                {/* Public pages: privacy & terms */}
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/terms" element={<TermsOfUsePage />} />

                {/* === ADD NEW PUBLIC ROUTE HERE === */}
                <Route path="/public/flight/:share_token" element={<PublicFlightDetailPage />} />
                {/* ================================= */}
                
                {/* Auth Callback Route - for Google authentication redirection */}
                <Route path="/auth/callback" element={<AuthCallback />} />

                {/* Protected Routes */}
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                } />
                          
                {/* Flight Routes */}
                <Route path="/flights" element={
                  <ProtectedRoute>
                    <FlightsPage />
                  </ProtectedRoute>
                } />
                <Route path="/flights/new" element={
                  <ProtectedRoute>
                    <NewFlightPage />
                  </ProtectedRoute>
                } />
                <Route path="/flights/:id/edit" element={
                  <ProtectedRoute>
                    <FlightEditPage />
                  </ProtectedRoute>
                } />
                <Route path="/flights/:id" element={
                  <ProtectedRoute>
                    <FlightDetailPage />
                  </ProtectedRoute>
                } />

                {/* Glider Routes */}
                <Route path="/gliders" element={
                  <ProtectedRoute>
                    <GlidersPage />
                  </ProtectedRoute>
                } />
                
                {/* Site and Weather Station Routes */}
                <Route path="/sites" element={
                  <ProtectedRoute>
                    <SitesPage />
                  </ProtectedRoute>
                } />
                <Route path="/balises" element={
                  <ProtectedRoute>
                    <BaliseManagerPage />
                  </ProtectedRoute>
                } />
                <Route path="/weather-stations" element={
                  <ProtectedRoute>
                    <WeatherStationsPage />
                  </ProtectedRoute>
                } />
                <Route path="/weather-forecast" element={
                  <ProtectedRoute>
                    <WeatherForecastPage />
                  </ProtectedRoute>
                } />
                <Route path="/meteo-spots" element={
                  <ProtectedRoute>
                    <MeteoSpotManagerPage />
                  </ProtectedRoute>
                } />
                <Route path="/thermal-hotspots" element={
                  <ProtectedRoute>
                    <ThermalHotspotsMapPage />
                  </ProtectedRoute>
                } />

                {/* Default Route */}
                <Route path="/" element={<Navigate to="/dashboard" />} />

                {/* 404 Route */}
                <Route path="*" element={<Navigate to="/dashboard" />} />
              </Routes>
              </Suspense>
              </TokenRefreshWrapper>
            </AuthProvider>
          </BrowserRouter>
        </div>
      </ResponsiveBackground>
    </MuiThemeProvider>
  );
};

const AuthNavigationListener: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const handleAuthRedirect = () => {
      navigate('/login', { replace: true });
    };

    window.addEventListener(AUTH_LOGIN_REDIRECT_EVENT, handleAuthRedirect);
    return () => {
      window.removeEventListener(AUTH_LOGIN_REDIRECT_EVENT, handleAuthRedirect);
    };
  }, [navigate]);

  return null;
};

// Protected route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading, isInitialized } = useAuth();

  // Show loading while authentication is being checked
  if (loading || !isInitialized) {
    return <LoadingOverlay open={true} global />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return <>{children}</>;
};

// Auth route component (redirect to dashboard if already logged in)
const AuthRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading, isInitialized } = useAuth();

  // Show loading while authentication is being checked
  if (loading || !isInitialized) {
    return <LoadingOverlay open={true} global />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" />;
  }

  return <>{children}</>;
};

// Auth callback handler
const AuthCallback: React.FC = () => {
  const { isAuthenticated, loading, isInitialized } = useAuth();
  
  if (loading || !isInitialized) {
    return <LoadingOverlay open={true} global />;
  }
  
  // Redirect to dashboard if authentication was successful
  if (isAuthenticated) {
    return <Navigate to="/dashboard" />;
  }
  
  // Otherwise redirect to login (with error if present in URL)
  return <Navigate to="/login" />;
};

function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}

export default App;
